/**
 * Utility functions for managing cookies
 */

/**
 * Deletes a cookie by setting its expiration date in the past
 * @param name - The name of the cookie to delete
 * @param domain - Optional domain for the cookie (defaults to current domain)
 * @param path - Optional path for the cookie (defaults to '/')
 */
export const deleteCookie = (name: string, domain?: string, path: string = '/'): void => {
  if (typeof document === 'undefined') return

  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`

  if (domain) {
    cookieString += `; domain=${domain}`
  }

  document.cookie = cookieString
}

/**
 * Sets a cookie with the specified name, value, and options
 * @param name - The name of the cookie
 * @param value - The value of the cookie
 * @param days - Optional number of days until the cookie expires
 * @param domain - Optional domain for the cookie
 * @param path - Optional path for the cookie (defaults to '/')
 */
export const setCookie = (
  name: string,
  value: string,
  days?: number,
  domain?: string,
  path: string = '/',
): void => {
  if (typeof document === 'undefined') return

  let cookieString = `${name}=${encodeURIComponent(value)}; path=${path}`

  if (days) {
    const expirationDate = new Date()
    expirationDate.setTime(expirationDate.getTime() + days * 24 * 60 * 60 * 1000)
    cookieString += `; expires=${expirationDate.toUTCString()}`
  }

  if (domain) {
    cookieString += `; domain=${domain}`
  }

  document.cookie = cookieString
}

/**
 * Gets the value of a cookie by name
 * @param name - The name of the cookie to retrieve
 * @returns The value of the cookie, or null if not found
 */
export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null

  const nameEQ = `${name}=`
  const cookies = document.cookie.split(';')

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i]
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length)
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length))
    }
  }

  return null
}
